import { cn, useContainerWidth, useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { useFloatingScrollbar } from "@tellescope/react-hooks";
import { CommonCalendarOptions, DateRange } from "../../types";
import { CalendarFilters } from "../../filters";
import { useCalendarEventsWithFilters } from "../../hooks";
import { useMemo } from "react";
import { startOfDay } from "date-fns/startOfDay";
import { MonthlyViewDayCell } from "./MonthlyViewDayCell";
import { getEventsByDate, getMonthGridDays } from "../../utils";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { useMonthViewDragLogic } from "./useMonthViewDragLogic";
import { MonthViewDragOverlay } from "./MonthViewDragOverlay";
import { addDays } from "date-fns/addDays";
import { startOfWeek } from "date-fns/startOfWeek";
import { useParams } from "react-router-dom";
import { UnavailableUsersWarningDialog } from "../dialogs";

export interface MonthViewProps extends DateRange, CommonCalendarOptions {
  referenceDate: Date;
  className?: string;
  timezone?: string;
  filters?: CalendarFilters;
  currentUserId?: string;
}

export const CalendarMonthView = ({
  startDate,
  referenceDate,
  weekStart = "sunday",
  className,
  timezone,
  filters,
  currentUserId,
}: MonthViewProps) => {
  const { format } = useDateFnsFormattersWithLocale();
  const gridDays = useMemo(() => getMonthGridDays(startDate, weekStart), [startDate, weekStart]);
  const params = useParams();
  const viewId = params.viewId;

  const eventsQuery = useCalendarEventsWithFilters({
    referenceDate,
    view: "month",
    timezone,
    weekStart,
    filters,
    currentUserId,
  });

  const eventsByDate = useMemo(() => getEventsByDate(eventsQuery.data ?? []), [eventsQuery.data]);

  const {
    sensors,
    activeDrag,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    updateWarningDialogProps,
  } = useMonthViewDragLogic({ timezone });

  const { scrollContainerRef, thumbRef, handleScroll } = useFloatingScrollbar({});
  const { ref: widthRef, width } = useContainerWidth<HTMLDivElement>();
  const variant = width > 0 && width < 770 ? "compact" : "default";

  return (
    <section data-testid="calendar-view-month" className={cn("flex flex-col min-h-0 overflow-hidden", className)}>
      {/* DAYS OF WEEK HEADER ROW */}
      <div className="grid grid-cols-7 divide-x flex-none">
        {Array.from({ length: 7 }, (_, i) => format(addDays(startOfWeek(startDate), i), "EEE")).map((day) => (
          <div
            key={`day-of-week-header-${day}`}
            className="flex items-center justify-center py-2 text-xs font-medium text-muted-foreground border-b border-b-gray-200"
          >
            {day}
          </div>
        ))}
      </div>
      {/* WEEKS ROWS */}
      <div ref={widthRef} className="relative flex-1 min-h-0 overflow-hidden">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className={cn("relative h-full w-full overflow-y-auto [&::-webkit-scrollbar]:hidden")}
          style={{ scrollbarWidth: "none" }}
        >
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div
              key={`weeks-rows-${startDate.getTime()}`}
              className={cn(
                "grid grid-cols-7",
                variant === "compact" ? "auto-rows-[minmax(80px,100px)]" : "auto-rows-[minmax(145px,1fr)]"
              )}
            >
              {gridDays.map(({ date, dayNumber, isOtherMonth, weekIndex, dayIndex }) => {
                const timestampForEvents = startOfDay(date).getTime();
                const events = eventsByDate[timestampForEvents] ?? [];

                return (
                  <MonthlyViewDayCell
                    key={`${date.getFullYear()}-${date.getMonth()}-day-${dayNumber}-cell`}
                    date={date}
                    dayNumber={dayNumber}
                    isOtherMonth={isOtherMonth}
                    events={events}
                    className={cn(dayIndex !== 0 && "border-l", weekIndex !== 0 && "border-t")}
                    onDayClick={`/calendar/view/${viewId}/day/${date.getFullYear()}/${date.getMonth()}/${date.getDate()}`}
                    getEventOnClick={(eventId) => ({
                      to: `.?eventId=${eventId}`,
                      replace: true,
                    })}
                    timezone={timezone}
                    variant={variant}
                  />
                );
              })}
            </div>
            <DragOverlay dropAnimation={null}>
              {activeDrag && (
                <MonthViewDragOverlay
                  event={activeDrag.event}
                  hoveredDayStart={activeDrag.hoveredDayStart}
                  timezone={timezone}
                />
              )}
            </DragOverlay>
          </DndContext>
        </div>
        <div
          ref={thumbRef}
          className="absolute top-0 right-1 w-1.5 bg-black/40 rounded-full pointer-events-none opacity-0 transition-opacity duration-300 z-[10000]"
        />
      </div>
      <UnavailableUsersWarningDialog {...updateWarningDialogProps} />
    </section>
  );
};
